import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Link,
  Button,
  HStack,
  Divider,
  SimpleGrid,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  fetchCustomerProfile,
  saveCustomerProfileStep,
  changeCustomerEmail,
  resetCustomerPassword,
} from "./applyProfileApi";
import { loadUserSession, saveUserSession, clearUserSession } from "./userSessionStorage";
import { clearApplyFormData, loadApplyFormData, saveApplyFormData } from "./applyFormStorage";
import { hasCompletedBankVerification, isCustomerPersonalInfoComplete } from "./customerApplicationStatus";

const formatCurrency = (value) => {
  const normalizedValue = Number(value);
  if (!Number.isFinite(normalizedValue) || normalizedValue <= 0) {
    return "—";
  }
  return `$${normalizedValue.toLocaleString()}`;
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString();
};

const renderValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
};

const stripUnenteredFields = (input) => {
  if (!input || typeof input !== "object") {
    return {};
  }

  return Object.entries(input).reduce((accumulator, [key, value]) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        accumulator[key] = trimmed;
      }
      return accumulator;
    }

    if (value !== null && value !== undefined && value !== "") {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});
};

const PROFILE_LOCKABLE_FIELDS = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "phoneNumber",
  "streetAddress",
  "city",
  "province",
  "postalCode",
];

const ADDRESS_EDITABLE_FIELDS = ["streetAddress", "city", "province", "postalCode"];

const PROVINCE_OPTIONS = [
  "Ontario",
  "Alberta",
  "British Columbia",
  "Manitoba",
  "Saskatchewan",
  "Nova Scotia",
  "New Brunswick",
  "Newfoundland and Labrador",
];

export default function UserProfilePage() {
  const navigate = useNavigate();
  const initialSession = useMemo(() => loadUserSession(), []);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [profile, setProfile] = useState(
    initialSession.customer && typeof initialSession.customer === "object" ? initialSession.customer : null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [profileValues, setProfileValues] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    phoneNumber: "",
    streetAddress: "",
    city: "",
    province: "",
    postalCode: "",
  });
  const [isSavingPersonalInfo, setIsSavingPersonalInfo] = useState(false);
  const [personalInfoMessage, setPersonalInfoMessage] = useState("");
  const [personalInfoError, setPersonalInfoError] = useState("");
  const [didAttemptSavePersonalInfo, setDidAttemptSavePersonalInfo] = useState(false);

  const [emailValues, setEmailValues] = useState({
    newEmail: "",
    currentPassword: "",
  });
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailError, setEmailError] = useState("");

  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    const auth = initialSession.auth && typeof initialSession.auth === "object" ? initialSession.auth : {};
    const customerId = auth.customerId || null;
    const email = auth.email || "";

    if (!customerId && !email) {
      setError("Please sign in to view your profile.");
      setIsLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError("");

        const payload = await fetchCustomerProfile({
          customerId,
          email,
        });

        const customer = payload.customer || null;
        setProfile(customer);

        if (customer) {
          saveUserSession({
            auth: {
              isAuthenticated: true,
              customerId: customer.id || customerId,
              email: customer.email || email,
            },
            customer,
          });
        }
      } catch (fetchError) {
        setError(fetchError.message || "Unable to load your profile right now. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [initialSession]);

  useEffect(() => {
    const personalInfo = profile && profile.personalInfo && typeof profile.personalInfo === "object" ? profile.personalInfo : {};

    setProfileValues({
      firstName: personalInfo.firstName || "",
      lastName: personalInfo.lastName || "",
      dateOfBirth: personalInfo.dateOfBirth || "",
      phoneNumber: personalInfo.phoneNumber || "",
      streetAddress: personalInfo.streetAddress || "",
      city: personalInfo.city || "",
      province: personalInfo.province || "",
      postalCode: personalInfo.postalCode || "",
    });
    setDidAttemptSavePersonalInfo(false);
  }, [profile]);

  const handleSignOut = () => {
    clearUserSession();
    clearApplyFormData();
    navigate("/sign-in");
  };

  if (isLoading) {
    return (
      <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
        <VStack maxW="760px" mx="auto" spacing={5} align="center">
          <Spinner size="lg" color="black" />
          <Text color="gray.600">Loading your profile...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
        <VStack maxW="760px" mx="auto" spacing={6} align="stretch">
          <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
            <VStack spacing={4} align="stretch" textAlign="center">
              <Heading size="lg">Profile unavailable</Heading>
              <Text color="red.500" fontSize="sm">{error || "Unable to load your profile."}</Text>
              <Button bg="black" color="white" borderRadius="full" h="50px" _hover={{ bg: "gray.900" }} onClick={() => navigate("/sign-in")}>
                Go to sign in
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  const personalInfo = profile.personalInfo && typeof profile.personalInfo === "object" ? profile.personalInfo : {};
  const incomeInfo = profile.incomeInfo && typeof profile.incomeInfo === "object" ? profile.incomeInfo : {};
  const bankInfo = profile.bankInfo && typeof profile.bankInfo === "object" ? profile.bankInfo : {};
  const firstName = typeof personalInfo.firstName === "string" ? personalInfo.firstName.trim() : "";
  const emailFallbackName = typeof profile.email === "string" ? profile.email.split("@")[0] : "";
  const greetingName = firstName || emailFallbackName || "there";
  const isProfileFieldLocked = (fieldName) => {
    if (ADDRESS_EDITABLE_FIELDS.includes(fieldName)) {
      return false;
    }

    const fieldValue = personalInfo[fieldName];
    return typeof fieldValue === "string" ? fieldValue.trim().length > 0 : Boolean(fieldValue);
  };
  const isProfileFieldEmpty = (fieldName) => {
    const fieldValue = profileValues[fieldName];
    return typeof fieldValue === "string" ? fieldValue.trim().length === 0 : !fieldValue;
  };
  const isProfileFieldInvalid = (fieldName) => didAttemptSavePersonalInfo && !isProfileFieldLocked(fieldName) && isProfileFieldEmpty(fieldName);
  const editableProfileFields = PROFILE_LOCKABLE_FIELDS.filter((fieldName) => !isProfileFieldLocked(fieldName));
  const hasEditableProfileFields = editableProfileFields.length > 0;
  const hasMissingProfileFields = PROFILE_LOCKABLE_FIELDS.some((fieldName) => isProfileFieldEmpty(fieldName));
  const isPersonalInfoComplete = isCustomerPersonalInfoComplete(profile);
  const hasCompletedApplication = hasCompletedBankVerification(profile);
  const loanStatusLabel = hasCompletedApplication ? "Submitted" : isPersonalInfoComplete ? "In progress" : "Profile incomplete";
  const loanActionPath = "/apply/reapply";
  const loanActionLabel = hasCompletedApplication ? "Reapply online" : "Continue application";
  const storedApply = loadApplyFormData();
  const storedPreliminary =
    storedApply.preliminary && typeof storedApply.preliminary === "object" ? storedApply.preliminary : {};
  const preliminaryLoanAmount = Number(storedPreliminary.loanAmount);
  const hasPreliminaryLoanAmount = Number.isFinite(preliminaryLoanAmount) && preliminaryLoanAmount > 0;
  const preferredLoanStatusAmount = hasPreliminaryLoanAmount ? preliminaryLoanAmount : profile.loanAmount;
  const profileProvince = typeof personalInfo.province === "string" ? personalInfo.province.trim() : "";
  const preferredLoanStatusProvince = profileProvince || profile.province || "";

  const loanHistory =
    hasCompletedApplication && (Number(profile.loanAmount) > 0 || profile.createdAt)
      ? [
          {
            id: "loan-1",
            amount: preferredLoanStatusAmount,
            province: preferredLoanStatusProvince,
            appliedOn: profile.createdAt,
            status: "Submitted",
          },
        ]
      : [];

  const hasLoanHistory = loanHistory.length > 0;

  const updateProfileState = (nextCustomer) => {
    if (!nextCustomer) {
      return;
    }

    setProfile(nextCustomer);

    const currentSession = loadUserSession();
    const currentAuth = currentSession.auth && typeof currentSession.auth === "object" ? currentSession.auth : {};

    const nextAuth = {
      isAuthenticated: true,
      customerId: nextCustomer.id || currentAuth.customerId || null,
      email: nextCustomer.email || currentAuth.email || "",
    };

    const currentApply = loadApplyFormData();
    const existingPreliminary =
      currentApply.preliminary && typeof currentApply.preliminary === "object" ? currentApply.preliminary : {};
    const nextPersonalInfo =
      nextCustomer.personalInfo && typeof nextCustomer.personalInfo === "object" ? nextCustomer.personalInfo : {};
    const profileProvince = typeof nextPersonalInfo.province === "string" ? nextPersonalInfo.province.trim() : "";
    const preferredLoanProvince = profileProvince || nextCustomer.province || existingPreliminary.province || "";

    saveUserSession({
      auth: nextAuth,
      customer: nextCustomer,
    });

    saveApplyFormData({
      auth: nextAuth,
      preliminary: {
        ...existingPreliminary,
        loanAmount: existingPreliminary.loanAmount || nextCustomer.loanAmount || "",
        province: preferredLoanProvince,
        email: nextCustomer.email || nextAuth.email || existingPreliminary.email || "",
      },
      customer: stripUnenteredFields(nextPersonalInfo),
      income: stripUnenteredFields(nextCustomer.incomeInfo),
      bank: stripUnenteredFields(nextCustomer.bankInfo),
    });
  };

  const handlePersonalInfoChange = (field) => (event) => {
    setProfileValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSavePersonalInfo = async () => {
    setPersonalInfoError("");
    setPersonalInfoMessage("");
    setDidAttemptSavePersonalInfo(true);

    if (!hasEditableProfileFields) {
      return;
    }

    if (hasMissingProfileFields) {
      setPersonalInfoError("Please fill in all profile fields.");
      return;
    }

    try {
      setIsSavingPersonalInfo(true);

      const filledProfileFields = editableProfileFields.reduce((accumulator, fieldName) => {
        const fieldValue = profileValues[fieldName];
        accumulator[fieldName] = typeof fieldValue === "string" ? fieldValue.trim() : fieldValue;
        return accumulator;
      }, {});

      const nextPersonalInfo = {
        ...personalInfo,
        ...filledProfileFields,
      };

      await saveCustomerProfileStep("customer", nextPersonalInfo);

      const nextCustomer = {
        ...profile,
        province: nextPersonalInfo.province || profile.province,
        personalInfo: nextPersonalInfo,
      };

      updateProfileState(nextCustomer);
      setPersonalInfoMessage("Personal information saved successfully.");
      setDidAttemptSavePersonalInfo(false);
      setActiveTabIndex(0);
    } catch (saveError) {
      setPersonalInfoError(saveError.message || "Unable to save personal information right now. Please try again.");
    } finally {
      setIsSavingPersonalInfo(false);
    }
  };

  const handleEmailChange = (field) => (event) => {
    setEmailValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    setEmailError("");
    setEmailSuccess("");

    const newEmail = emailValues.newEmail.trim().toLowerCase();
    const currentPassword = emailValues.currentPassword;

    if (!newEmail || !currentPassword) {
      setEmailError("New email and current password are required.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailError("Enter a valid email address.");
      return;
    }

    try {
      setIsChangingEmail(true);

      const payload = await changeCustomerEmail({
        newEmail,
        currentPassword,
        identity: {
          customerId: profile.id,
          email: profile.email,
        },
      });

      const nextCustomer = payload.customer || {
        ...profile,
        email: newEmail,
      };

      updateProfileState(nextCustomer);
      setEmailSuccess("Email updated successfully.");
      setEmailValues({ newEmail: "", currentPassword: "" });
    } catch (changeError) {
      setEmailError(changeError.message || "Unable to change email right now. Please try again.");
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    const { currentPassword, newPassword, confirmPassword } = passwordValues;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All password fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password must match.");
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(newPassword)) {
      setPasswordError("Password must be at least 8 characters and include letters and numbers.");
      return;
    }

    try {
      setIsResettingPassword(true);

      await resetCustomerPassword({
        currentPassword,
        newPassword,
        confirmPassword,
        identity: {
          customerId: profile.id,
          email: profile.email,
        },
      });

      setPasswordSuccess("Password updated successfully.");
      setPasswordValues({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (resetError) {
      setPasswordError(resetError.message || "Unable to reset password right now. Please try again.");
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="980px" mx="auto" spacing={6} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <HStack justify="space-between" align="start">
              <VStack spacing={1} align="start">
                <Heading size="lg">{`Welcome back ${greetingName}`}</Heading>
                <Text color="gray.600" fontSize="sm">Signed in as {renderValue(profile.email)}</Text>
              </VStack>
              <Button variant="outline" borderColor="gray.300" color="gray.700" borderRadius="full" onClick={handleSignOut}>
                Sign out
              </Button>
            </HStack>

            {!isPersonalInfoComplete && (
              <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="gray.50" px={{ base: 4, md: 5 }} py={4}>
                <VStack align="start" spacing={3}>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                    px={3}
                    py={1}
                    borderRadius="full"
                    bg="black"
                    color="white"
                  >
                    Complete your profile
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    Please click on the "My Profile" tab to add your information before continuing your loan application.
                  </Text>
                </VStack>
              </Box>
            )}

            <Divider />

            <Tabs variant="enclosed" colorScheme="gray" isFitted index={activeTabIndex} onChange={setActiveTabIndex}>
              <TabList overflowX="auto" whiteSpace="nowrap">
                <Tab>My Account</Tab>
                <Tab>My Profile</Tab>
                <Tab>Income &amp; Bank</Tab>
                <Tab>Security Settings</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0} pt={5}>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="sm">Loan Section</Heading>

                    <Tabs size="sm" variant="soft-rounded" colorScheme="gray">
                      <TabList>
                        <Tab>Loan Status</Tab>
                        <Tab>Loan History</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel px={0} pt={4}>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <Text color="gray.700">Current status: <strong>{loanStatusLabel}</strong></Text>
                            <Text color="gray.700">Amount requested: <strong>{formatCurrency(preferredLoanStatusAmount)}</strong></Text>
                            <Text color="gray.700">Province: <strong>{renderValue(preferredLoanStatusProvince)}</strong></Text>
                            <Text color="gray.700">Last updated: <strong>{formatDate(profile.updatedAt)}</strong></Text>
                          </SimpleGrid>
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                          {hasLoanHistory ? (
                            <TableContainer border="1px solid" borderColor="gray.200" borderRadius="lg">
                              <Table size="sm" variant="simple">
                                <Thead bg="gray.50">
                                  <Tr>
                                    <Th>Applied On</Th>
                                    <Th isNumeric>Amount</Th>
                                    <Th>Province</Th>
                                    <Th>Status</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {loanHistory.map((item) => (
                                    <Tr key={item.id}>
                                      <Td>{formatDate(item.appliedOn)}</Td>
                                      <Td isNumeric>{formatCurrency(item.amount)}</Td>
                                      <Td>{renderValue(item.province)}</Td>
                                      <Td>{renderValue(item.status)}</Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Text color="gray.600">No loan application history yet.</Text>
                          )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>

                    <Button
                      alignSelf="start"
                      bg="black"
                      color="white"
                      borderRadius="full"
                      px={7}
                      h="46px"
                      _hover={{ bg: "gray.900" }}
                      onClick={() => navigate(loanActionPath)}
                    >
                      {loanActionLabel}
                    </Button>
                  </VStack>
                </TabPanel>

                <TabPanel px={0} pt={5}>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="sm">Personal Information</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl isInvalid={isProfileFieldInvalid("firstName")}>
                        <FormLabel fontSize="sm">First name</FormLabel>
                        <Input
                          value={profileValues.firstName}
                          onChange={handlePersonalInfoChange("firstName")}
                          isReadOnly={isProfileFieldLocked("firstName")}
                          bg={isProfileFieldLocked("firstName") ? "gray.50" : "white"}
                        />
                        <FormErrorMessage>First name is required.</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={isProfileFieldInvalid("lastName")}>
                        <FormLabel fontSize="sm">Last name</FormLabel>
                        <Input
                          value={profileValues.lastName}
                          onChange={handlePersonalInfoChange("lastName")}
                          isReadOnly={isProfileFieldLocked("lastName")}
                          bg={isProfileFieldLocked("lastName") ? "gray.50" : "white"}
                        />
                        <FormErrorMessage>Last name is required.</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={isProfileFieldInvalid("dateOfBirth")}>
                        <FormLabel fontSize="sm">Date of birth</FormLabel>
                        <Input
                          type="date"
                          value={profileValues.dateOfBirth}
                          onChange={handlePersonalInfoChange("dateOfBirth")}
                          isReadOnly={isProfileFieldLocked("dateOfBirth")}
                          bg={isProfileFieldLocked("dateOfBirth") ? "gray.50" : "white"}
                        />
                        <FormErrorMessage>Date of birth is required.</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={isProfileFieldInvalid("phoneNumber")}>
                        <FormLabel fontSize="sm">Phone number</FormLabel>
                        <Input
                          value={profileValues.phoneNumber}
                          onChange={handlePersonalInfoChange("phoneNumber")}
                          isReadOnly={isProfileFieldLocked("phoneNumber")}
                          bg={isProfileFieldLocked("phoneNumber") ? "gray.50" : "white"}
                        />
                        <FormErrorMessage>Phone number is required.</FormErrorMessage>
                      </FormControl>
                    </SimpleGrid>

                    <Divider />

                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm">Address</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl isInvalid={isProfileFieldInvalid("streetAddress")}>
                          <FormLabel fontSize="sm">Street address</FormLabel>
                          <Input
                            value={profileValues.streetAddress}
                            onChange={handlePersonalInfoChange("streetAddress")}
                            isReadOnly={isProfileFieldLocked("streetAddress")}
                            bg={isProfileFieldLocked("streetAddress") ? "gray.50" : "white"}
                          />
                          <FormErrorMessage>Street address is required.</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={isProfileFieldInvalid("city")}>
                          <FormLabel fontSize="sm">City</FormLabel>
                          <Input
                            value={profileValues.city}
                            onChange={handlePersonalInfoChange("city")}
                            isReadOnly={isProfileFieldLocked("city")}
                            bg={isProfileFieldLocked("city") ? "gray.50" : "white"}
                          />
                          <FormErrorMessage>City is required.</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={isProfileFieldInvalid("province")}>
                          <FormLabel fontSize="sm">Province</FormLabel>
                          <Select
                            value={profileValues.province}
                            onChange={handlePersonalInfoChange("province")}
                            isDisabled={isProfileFieldLocked("province")}
                            bg={isProfileFieldLocked("province") ? "gray.50" : "white"}
                            borderColor="gray.300"
                            _focus={{ borderColor: "black", boxShadow: "none" }}
                            _hover={{ borderColor: "gray.400" }}
                          >
                            <option value="" disabled hidden />
                            {profileValues.province && !PROVINCE_OPTIONS.includes(profileValues.province) && (
                              <option value={profileValues.province}>{profileValues.province}</option>
                            )}
                            {PROVINCE_OPTIONS.map((province) => (
                              <option key={province} value={province}>
                                {province}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>Province is required.</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={isProfileFieldInvalid("postalCode")}>
                          <FormLabel fontSize="sm">Postal code</FormLabel>
                          <Input
                            value={profileValues.postalCode}
                            onChange={handlePersonalInfoChange("postalCode")}
                            isReadOnly={isProfileFieldLocked("postalCode")}
                            bg={isProfileFieldLocked("postalCode") ? "gray.50" : "white"}
                          />
                          <FormErrorMessage>Postal code is required.</FormErrorMessage>
                        </FormControl>
                      </SimpleGrid>

                      <VStack align="start" spacing={2}>
                        {hasEditableProfileFields && (
                          <Button isLoading={isSavingPersonalInfo} bg="black" color="white" borderRadius="full" px={7} h="44px" _hover={{ bg: "gray.900" }} onClick={handleSavePersonalInfo}>
                            Save personal information
                          </Button>
                        )}
                        {personalInfoMessage && <Text color="green.600" fontSize="sm">{personalInfoMessage}</Text>}
                        {personalInfoError && <Text color="red.500" fontSize="sm">{personalInfoError}</Text>}
                      </VStack>
                    </VStack>
                  </VStack>
                </TabPanel>

                <TabPanel px={0} pt={5}>
                  <VStack align="stretch" spacing={5}>
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm">Income Information</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Text color="gray.700">Income type: <strong>{renderValue(incomeInfo.incomeType)}</strong></Text>
                        <Text color="gray.700">Net monthly income: <strong>{formatCurrency(incomeInfo.netMonthlyIncome)}</strong></Text>
                        <Text color="gray.700">Payroll type: <strong>{renderValue(incomeInfo.payrollType)}</strong></Text>
                        <Text color="gray.700">Payment frequency: <strong>{renderValue(incomeInfo.paymentFrequency)}</strong></Text>
                      </SimpleGrid>
                    </VStack>

                    <Divider />

                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm">Bank Information</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Text color="gray.700">Bank name: <strong>{renderValue(bankInfo.financialInstitution)}</strong></Text>
                        <Text color="gray.700">Transit number: <strong>{renderValue(bankInfo.transitNumber)}</strong></Text>
                        <Text color="gray.700">Account number: <strong>{renderValue(bankInfo.accountNumber)}</strong></Text>
                      </SimpleGrid>
                    </VStack>
                  </VStack>
                </TabPanel>

                <TabPanel px={0} pt={5}>
                  <VStack align="stretch" spacing={6}>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                      <VStack align="stretch" spacing={3}>
                        <Heading size="sm">Change Email</Heading>
                        <form onSubmit={handleChangeEmail}>
                          <VStack align="stretch" spacing={3}>
                            <FormControl>
                              <FormLabel fontSize="sm">New email</FormLabel>
                              <Input type="email" value={emailValues.newEmail} onChange={handleEmailChange("newEmail")} required />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="sm">Current password</FormLabel>
                              <Input type="password" value={emailValues.currentPassword} onChange={handleEmailChange("currentPassword")} required />
                            </FormControl>
                            <HStack spacing={3}>
                              <Button type="submit" isLoading={isChangingEmail} bg="black" color="white" borderRadius="full" px={7} h="42px" _hover={{ bg: "gray.900" }}>
                                Update email
                              </Button>
                              {emailSuccess && <Text color="green.600" fontSize="sm">{emailSuccess}</Text>}
                              {emailError && <Text color="red.500" fontSize="sm">{emailError}</Text>}
                            </HStack>
                          </VStack>
                        </form>
                      </VStack>
                    </Box>

                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                      <VStack align="stretch" spacing={3}>
                        <Heading size="sm">Reset Password</Heading>
                        <form onSubmit={handleResetPassword}>
                          <VStack align="stretch" spacing={3}>
                            <FormControl>
                              <FormLabel fontSize="sm">Current password</FormLabel>
                              <Input type="password" value={passwordValues.currentPassword} onChange={handlePasswordChange("currentPassword")} required />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="sm">New password</FormLabel>
                              <Input type="password" value={passwordValues.newPassword} onChange={handlePasswordChange("newPassword")} required />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="sm">Confirm new password</FormLabel>
                              <Input type="password" value={passwordValues.confirmPassword} onChange={handlePasswordChange("confirmPassword")} required />
                            </FormControl>
                            <Link
                              as={RouterLink}
                              to="/reset-password"
                              color="blue.600"
                              fontSize="sm"
                              textDecoration="underline"
                              _hover={{ color: "blue.700" }}
                              alignSelf="start"
                            >
                              Forgot password?
                            </Link>
                            <HStack spacing={3}>
                              <Button type="submit" isLoading={isResettingPassword} bg="black" color="white" borderRadius="full" px={7} h="42px" _hover={{ bg: "gray.900" }}>
                                Reset password
                              </Button>
                              {passwordSuccess && <Text color="green.600" fontSize="sm">{passwordSuccess}</Text>}
                              {passwordError && <Text color="red.500" fontSize="sm">{passwordError}</Text>}
                            </HStack>
                          </VStack>
                        </form>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
